import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { dataService } from '../services/dataService'
import { offlineDB } from '../services/offlineDB'
import { Truck, Check, WifiOff } from 'lucide-react'
import { obtenerOrdenAprendido } from '../services/ordenRutas'
import AlbaranDialog from './AlbaranDialog'

export default function Reparto({ usuario, rutaInicial, isOnline }) {
  const [clientes, setClientes] = useState([])
  const [productos, setProductos] = useState([])
  const [clientPricesMap, setClientPricesMap] = useState({})
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10))
  const [rutaSeleccionada, setRutaSeleccionada] = useState('')
  const [rutas, setRutas] = useState([])
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null)
  const [albaranesHoy, setAlbaranesHoy] = useState([])
  const [loading, setLoading] = useState(true)
  const [dataSource, setDataSource] = useState('online')

  const diasSemanaMap = {
    0: 'domingo',
    1: 'lunes',
    2: 'martes',
    3: 'miercoles',
    4: 'jueves',
    5: 'viernes',
    6: 'sabado'
  }

  useEffect(() => {
    cargarDatos()
  }, [fecha, rutaSeleccionada])

  async function cargarDatos() {
    setLoading(true)
    try {
      console.log('🔄 Cargando datos...', isOnline ? 'ONLINE' : 'OFFLINE')
      
      // Cargar clientes, productos y precios especiales en paralelo
      const [clientesRes, productosRes, clientPricesRes] = await Promise.all([
        dataService.getClients(),
        dataService.getProducts(),
        supabase.from('client_prices').select('client_id, product_id, pvp').eq('activo', true)
      ])

      console.log('✅ Clientes cargados:', clientesRes.data?.length, 'desde', clientesRes.source)
      console.log('✅ Productos cargados:', productosRes.data?.length, 'desde', productosRes.source)
      console.log('✅ Precios especiales cargados:', clientPricesRes.data?.length || 0)

      // Construir mapa de precios especiales: { client_id: { product_id: pvp } }
      const pricesMap = {}
      ;(clientPricesRes.data || []).forEach(cp => {
        if (!pricesMap[cp.client_id]) pricesMap[cp.client_id] = {}
        pricesMap[cp.client_id][cp.product_id] = Number(cp.pvp)
      })
      setClientPricesMap(pricesMap)

      setDataSource(clientesRes.source)
      setProductos(productosRes.data || [])

      // Obtener rutas de los clientes
      const rutasUnicas = [...new Set(clientesRes.data?.map(c => c.ruta).filter(Boolean))].sort()
      setRutas(rutasUnicas)
      
      if (!rutaSeleccionada) {
        if (rutaInicial && rutasUnicas.includes(rutaInicial)) {
          setRutaSeleccionada(rutaInicial)
        } else if (rutasUnicas.length > 0) {
          setRutaSeleccionada(rutasUnicas[0])
        }
      }

      // Cargar albaranes del día (online y offline)
      let albaranesIds = new Set()
      
      if (isOnline) {
        try {
          const { data: albaranesRes } = await supabase
            .from('deliveries')
            .select('client_id')
            .eq('fecha', fecha)
          
          albaranesIds = new Set(albaranesRes?.map(a => a.client_id) || [])
          console.log('✅ Albaranes online:', albaranesIds.size)
        } catch (err) {
          console.log('⚠️ Error cargando albaranes online')
        }
      }
      
      // Agregar albaranes guardados offline
      const albaranesOffline = await offlineDB.getDeliveriesOffline()
      albaranesOffline.forEach(d => {
        if (d.fecha === fecha) {
          albaranesIds.add(d.client_id)
        }
      })
      console.log('✅ Albaranes totales (online + offline):', albaranesIds.size)

      setAlbaranesHoy(albaranesIds)

      const ordenAprendido = isOnline 
        ? await obtenerOrdenAprendido(rutaSeleccionada || rutaInicial || rutasUnicas[0], fecha)
        : {}

      console.log('🧠 Orden aprendido:', ordenAprendido)

      const diaActual = diasSemanaMap[new Date(fecha + 'T00:00:00').getDay()]
      
      const clientesFiltrados = (clientesRes.data || [])
        .filter(c => c.ruta === (rutaSeleccionada || rutaInicial || rutasUnicas[0]))
        .map(c => ({
          ...c,
          estado: getEstadoCliente(c, diaActual, albaranesIds),
          ordenAprendido: ordenAprendido[c.id] || 999
        }))
        .sort((a, b) => {
          const ordenEstado = { 'por_repartir': 0, 'repartido': 1, 'descanso': 2 }
          if (ordenEstado[a.estado] !== ordenEstado[b.estado]) {
            return ordenEstado[a.estado] - ordenEstado[b.estado]
          }
          if (a.estado === 'por_repartir' && a.ordenAprendido !== b.ordenAprendido) {
            return a.ordenAprendido - b.ordenAprendido
          }
          return a.alias.localeCompare(b.alias)
        })

      setClientes(clientesFiltrados)
      console.log('✅ Clientes ordenados:', clientesFiltrados.length)

      if (Object.keys(ordenAprendido).length > 0) {
        console.log('📊 Orden aprendido aplicado:')
        clientesFiltrados
          .filter(c => c.estado === 'por_repartir')
          .slice(0, 5)
          .forEach(c => {
            console.log(`  ${c.alias}: orden ${c.ordenAprendido === 999 ? 'sin datos' : c.ordenAprendido.toFixed(1)}`)
          })
      }
    } catch (error) {
      console.error('❌ Error cargando datos:', error)
      alert('Error al cargar datos: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  function getEstadoCliente(cliente, diaActual, albaranesIds) {
    const trabajaHoy = cliente.dias_trabajo?.includes(diaActual)
    const tieneAlbaran = albaranesIds.has(cliente.id)

    if (!trabajaHoy) return 'descanso'
    if (tieneAlbaran) return 'repartido'
    return 'por_repartir'
  }

  function getIconoEstado(estado) {
    switch (estado) {
      case 'por_repartir':
        return <Truck size={18} style={{ color: 'var(--accent)' }} />
      case 'repartido':
        return <Check size={20} style={{ color: '#10b981', strokeWidth: 3 }} />
      case 'descanso':
        return <span style={{ fontSize: '1.125rem' }}>💤</span>
      default:
        return null
    }
  }

  function getTextoEstado(estado) {
    switch (estado) {
      case 'por_repartir': return 'Por repartir'
      case 'repartido': return 'Repartido'
      case 'descanso': return 'Descanso'
      default: return ''
    }
  }

  return (
    <div>
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'end' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
            Fecha de reparto
          </label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            style={{
              width: '100%',
              padding: '0.625rem',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--line)',
              borderRadius: '6px',
              color: 'var(--fg)',
              fontSize: '1rem'
            }}
          />
        </div>

        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
            Ruta
          </label>
          <select
            value={rutaSeleccionada}
            onChange={(e) => setRutaSeleccionada(e.target.value)}
            style={{
              width: '100%',
              padding: '0.625rem',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--line)',
              borderRadius: '6px',
              color: 'var(--fg)',
              fontSize: '1rem'
            }}
          >
            {rutas.map(ruta => (
              <option key={ruta} value={ruta}>{ruta}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="tabla-container">
        <div className="tabla-scroll">
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
              Cargando clientes...
            </div>
          ) : clientes.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
              No hay clientes en esta ruta
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: '48px' }}></th>
                  <th>Cliente</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map(cliente => (
                  <tr
                    key={cliente.id}
                    onClick={() => setClienteSeleccionado(cliente)}
                    className={cliente.estado === 'repartido' ? 'row-repartido' : cliente.estado === 'descanso' ? 'row-descanso' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      {getIconoEstado(cliente.estado)}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                        {cliente.alias || cliente.razon_social}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.2rem' }}>
                        {getTextoEstado(cliente.estado)}
                        {cliente.direccion ? <span style={{ marginLeft: '0.5rem' }}>· 📍 {cliente.direccion}</span> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {clienteSeleccionado && (
        <AlbaranDialog
          cliente={clienteSeleccionado}
          productos={productos}
          clientPrices={clientPricesMap[clienteSeleccionado?.id] || {}}
          fecha={fecha}
          isOnline={isOnline}
          onClose={() => {
            setClienteSeleccionado(null)
            cargarDatos()
          }}
        />
      )}
    </div>
  )
}